import { type FC, useContext } from 'react';
import { useMatch } from 'react-router-dom';
import cn from 'classnames';

import { AppRoute } from '@/common/app/routes';
import { timeStringFormatShortForm } from '@/common/lib/timer';
import { type ArenaOffersDto } from '@/common/types/shop/marketBoxes';
import { ModalContext } from '@/components/Modals/Modal/config';
import { Picture } from '@/components/ui/Picture/Picture';

import arenaOfferPng from '/img/shop/arenaOffers/arena_offer_icon.png';
import arenaOfferWebp from '/img/shop/arenaOffers/arena_offer_icon.webp';
import profitDropPng from '/img/shop/arenaOffers/profitDropIcon.png';
import profitDropWebp from '/img/shop/arenaOffers/profitDropIcon.webp';
import startOfferPng from '/img/shop/arenaOffers/start_offer_icon.png';
import startOfferWebp from '/img/shop/arenaOffers/start_offer_icon.webp';
import weekendOfferPng from '/img/shop/arenaOffers/weekend_offer_icon.png';
import weekendOfferWebp from '/img/shop/arenaOffers/weekend_offer_icon.webp';

interface OfferDiscountBadgeProps {
  className?: string;
  arenaOffer: ArenaOffersDto;
  type: 'weekend' | 'start' | 'arena' | 'profitDrop';
}

const images: Record<
  OfferDiscountBadgeProps['type'],
  { png: string; webp: string }
> = {
  arena: { png: arenaOfferPng, webp: arenaOfferWebp },
  start: { png: startOfferPng, webp: startOfferWebp },
  weekend: { png: weekendOfferPng, webp: weekendOfferWebp },
  profitDrop: { png: profitDropPng, webp: profitDropWebp },
};

const colorsConfig: Partial<Record<OfferDiscountBadgeProps['type'], string>> = {
  profitDrop: '#50FFCF',
};

const OfferDiscountBadge: FC<OfferDiscountBadgeProps> = ({
  className,
  arenaOffer,
  type,
}) => {
  const { openModal } = useContext(ModalContext);
  const isHome = useMatch(AppRoute.MAIN);

  const handleClick = (): void => {
    openModal({
      renderKind: 'arenaOfferPopup',
      props: { type: type === 'profitDrop' ? 'profitDrop' : 'arena' },
    });
  };

  if (!isHome) return null;

  return (
    <button
      className={cn(
        'h-16 flex flex-col pointer-events-auto items-center',
        className,
      )}
      onClick={handleClick}
    >
      <div className="w-16 size-12 relative flex items-center justify-center">
        <Picture
          className="absolute w-[125%] mt-2"
          alt="discount icon"
          img={images[type].png}
          webp={images[type].webp}
        />
      </div>
      <div
        className="-mt-2 h-6 rounded-full bg-black/60 backdrop-blur-2xl flex items-center justify-center text-xs font-bold px-2 shadow-glass-blur"
        style={{
          color: colorsConfig[type] ?? '#82E024',
        }}
      >
        {timeStringFormatShortForm(
          arenaOffer.discountTimeLeftInSeconds * 1000,
          true,
        )}
      </div>
    </button>
  );
};

export default OfferDiscountBadge;
